import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { publicAPI } from '../lib/api';

/**
 * App-wide tracking pixel loader.
 * Fetches Meta Pixel ID + custom tracking-head-code from the public landing-page
 * settings and injects them into <head> exactly ONCE per session. Fires
 * fbq('track','PageView') on every SPA route change so Meta Ads sees every page.
 * Mount inside <BrowserRouter> at the top level of the app so it applies to
 * every route (marketer, admin, back-office, public).
 */
const TrackingPixels = () => {
  const location = useLocation();
  const configRef = useRef({ pixelId: '', customCode: '', loaded: false });

  // 1. Load config once on mount and inject pixel + custom code
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await publicAPI.getLandingPage();
        if (cancelled) return;
        const pixelId = (res.data?.meta_pixel_id || '').trim();
        const customCode = res.data?.tracking_head_code || '';
        configRef.current = { pixelId, customCode, loaded: true };

        // ---- Inject Meta Pixel (once) ----
        if (pixelId && !window.fbq) {
          const s = document.createElement('script');
          s.setAttribute('data-medichi-pixel', 'true');
          s.text = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${pixelId}');
            fbq('track', 'PageView');
          `;
          document.head.appendChild(s);

          const ns = document.createElement('noscript');
          ns.setAttribute('data-medichi-pixel', 'true');
          ns.innerHTML = `<img height="1" width="1" style="display:none" alt="fb" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
          document.head.appendChild(ns);
        }

        // ---- Inject custom tracking snippet (GA4, TikTok, etc.) ----
        if (customCode) {
          const wrapper = document.createElement('div');
          wrapper.setAttribute('data-medichi-tracking', 'true');
          wrapper.style.display = 'none';
          wrapper.innerHTML = customCode;
          // Move any <script> nodes out so they actually execute
          Array.from(wrapper.querySelectorAll('script')).forEach((oldS) => {
            const newS = document.createElement('script');
            Array.from(oldS.attributes).forEach((a) => newS.setAttribute(a.name, a.value));
            newS.text = oldS.text;
            document.head.appendChild(newS);
            oldS.remove();
          });
          document.head.appendChild(wrapper);
        }
      } catch (e) {
        // Silent fail — pixel not critical for app function
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2. Fire a PageView on every SPA route change (Meta needs this — SPA routes don't reload)
  useEffect(() => {
    if (configRef.current.loaded && configRef.current.pixelId && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, location.search]);

  return null;
};

export default TrackingPixels;
